import { api } from "@/lib/api";
import { useApiQuery } from "@/lib/use-api-query";
import { File, Folder } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

type Entry = { name: string; path: string; type: "dir" | "file" };
type Listing = { path: string; entries: Entry[] };

export type PickedFile = { name: string; path: string };

/** Browses the project's Seafile folder (only what the viewer may see) and picks a file or folder. */
export function FileLinkPicker({
  projectId,
  onPick,
  onClose,
}: {
  projectId: string;
  onPick: (picked: PickedFile) => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const [path, setPath] = useState("");
  const { data, error } = useApiQuery(
    () =>
      api.get<Listing>(
        `/api/projects/${encodeURIComponent(projectId)}/files?path=${encodeURIComponent(path)}`,
      ),
    [projectId, path],
  );
  const crumbs = path ? path.split("/") : [];

  return (
    <div
      data-testid="file-link-picker"
      className="absolute left-3 top-2 z-30 w-80 rounded-md border border-input bg-popover text-popover-foreground shadow-lg"
    >
      <div className="flex items-center justify-between gap-2 border-b border-input px-3 py-1.5 text-xs">
        <span className="font-medium">{t("workItemDetail.editor.filePickerTitle")}</span>
        <button
          type="button"
          onClick={onClose}
          className="text-muted-foreground hover:text-foreground"
        >
          {t("workItemDetail.editor.filePickerClose")}
        </button>
      </div>
      <div
        data-testid="file-link-picker-breadcrumbs"
        className="flex flex-wrap items-center gap-1 px-3 py-1 text-xs text-muted-foreground"
      >
        <button type="button" onClick={() => setPath("")} className="hover:text-foreground">
          {t("workItemDetail.editor.filePickerRoot")}
        </button>
        {crumbs.map((c, i) => (
          <span key={crumbs.slice(0, i + 1).join("/")} className="flex items-center gap-1">
            /
            <button
              type="button"
              onClick={() => setPath(crumbs.slice(0, i + 1).join("/"))}
              className="hover:text-foreground"
            >
              {c}
            </button>
          </span>
        ))}
      </div>
      <ul className="max-h-56 overflow-auto">
        {error && (
          <li className="px-3 py-1.5 text-xs text-destructive">
            {t("workItemDetail.editor.filePickerFailed")}
          </li>
        )}
        {data && data.entries.length === 0 && (
          <li className="px-3 py-1.5 text-xs text-muted-foreground">
            {t("workItemDetail.editor.filePickerEmpty")}
          </li>
        )}
        {data?.entries.map((e) => (
          <li key={e.path} className="flex items-center hover:bg-accent/40">
            <button
              type="button"
              data-testid="file-link-picker-entry"
              onClick={() =>
                e.type === "dir" ? setPath(e.path) : onPick({ name: e.name, path: e.path })
              }
              className="flex min-w-0 flex-1 items-center gap-2 px-3 py-1.5 text-left text-sm"
            >
              {e.type === "dir" ? (
                <Folder className="h-3.5 w-3.5 shrink-0" />
              ) : (
                <File className="h-3.5 w-3.5 shrink-0" />
              )}
              <span className="truncate">{e.name}</span>
            </button>
          </li>
        ))}
      </ul>
      {path && (
        <div className="border-t border-input px-3 py-1.5">
          <button
            type="button"
            data-testid="file-link-picker-use-folder"
            onClick={() => onPick({ name: crumbs[crumbs.length - 1] ?? path, path })}
            className="text-xs text-primary hover:underline"
          >
            {t("workItemDetail.editor.filePickerUseFolder")}
          </button>
        </div>
      )}
    </div>
  );
}
